// =============================================================================
// forecast.js — the forecaster's sums, rebuilt on our side.
//
// This is a faithful copy of the V2 four-signal engine that Trafford and Arndale
// run today inside Google Apps Script (ManchesterForecastEngine, buildForecast).
// It is BUILD-FORWARD ONLY: nothing in this file is wired to a screen, nothing
// live calls it, and the real forecaster is untouched.
//
// Why it takes its signals as arguments rather than fetching them: the four
// signals are built from Square's sales history, and there is no Square
// credential in this repo tree (see SCHEMA_NOTES_forecast.md §5). Splitting
// "work out the signals" from "do the sums" means the sums can be proved right
// today against 310 real days of the live engine's own recorded workings, and
// the signal-building can be dropped in later without touching this maths.
//
// FAITHFUL, NOT IMPROVED. Where the live engine does something odd, this copies
// the odd thing. Two known examples, both flagged for Adam rather than fixed:
//   * Square's per-item quantity is summed and treated as "portions", and rolls
//     are then half of that.
//   * When there's no previous-year data the engine moves that signal's weight
//     onto the six-week average but still divides by the full 8.0.
// Changing either would silently change every number the kitchen sees.
// =============================================================================

// ---- the constants, exactly as the live engine holds them -------------------
// Kept here as the default so the library stands alone; the same numbers live in
// the forecast_config table (migration 0082) so they can be changed without a
// deploy, and loadConfig() below takes that shape.
export const DEFAULT_CONFIG = {
  weights: { lastSameDay: 2.0, sixWeekAvg: 3.5, prevYear: 1.5, baseline: 1.0 },
  outlierThreshold: 0.35,
  stockoutTiers: [
    { hoursBeforeClose: 2, uplift: 0.20 },
    { hoursBeforeClose: 1, uplift: 0.10 },
    { hoursBeforeClose: 0, uplift: 0.05 },
  ],
  closingTimes: {
    TC:      { 0: 18.0, 1: 21.0, 2: 21.0, 3: 21.0, 4: 21.0, 5: 21.0, 6: 21.0 },
    Arndale: { 0: 17.5, 1: 19.0, 2: 19.0, 3: 19.0, 4: 19.0, 5: 19.0, 6: 19.0 },
  },
  baselines: {
    TC:      { 0: 375, 1: 260, 2: 260, 3: 260, 4: 260, 5: 375, 6: 650 },
    Arndale: { 0: 185, 1: 185, 2: 185, 3: 185, 4: 185, 5: 190, 6: 260 },
  },
  floors: { Arndale: 180 },
  tgtgBuffer: { TC: 38, Arndale: 38 },
  newSkuMinimum: { TC: 10, Arndale: 8 },
  wasteThreshold: 0.20,
  wasteTrim: 0.05,
  context: {
    rain:            { TC: 0.10,  Arndale: -0.05 },
    sunny:           { TC: -0.08, Arndale: 0.08 },
    bankHoliday:     { TC: 0.20,  Arndale: 0.20 },
    schoolHoliday:   { TC: 0.20,  Arndale: 0.20 },
    ramadan:         { TC: -0.08, Arndale: -0.08 },
    mothersDay:      { TC: -0.15, Arndale: -0.15 },
    blackFridayPeak: { TC: 0.25,  Arndale: 0.25 },
    blackFridayWeek: { TC: 0.10,  Arndale: 0.10 },
    payday:          { TC: 0.08,  Arndale: 0.08 },
    januarySales:    { TC: 0.12,  Arndale: 0.12 },
  },
  schoolHolidays: [
    { start: "2026-02-16", end: "2026-02-20" },
    { start: "2026-04-06", end: "2026-04-17" },
    { start: "2026-05-25", end: "2026-05-29" },
    { start: "2026-07-20", end: "2026-08-31" },
    { start: "2026-10-26", end: "2026-10-30" },
    { start: "2026-12-21", end: "2026-12-31" },
  ],
  ramadan: { start: "2026-02-18", end: "2026-03-19" },
  // The order the kitchen wants the roll list in. Anything not named falls to
  // the bottom, in the order it arrived.
  preferredOrder: [
    "Crispy Chicken", "Teriyaki Chicken", "Shrimp", "Bondi",
    "Cucumber Avo", "Tempura Eggplant", "Salmon Smash Avo", "Cooked Tuna",
    "Spicy Tuna", "Chicken Avo", "Tuna Avo", "Miso Honey Salmon",
    "Club Chicken", "Lemon Pepper Prawn",
  ],
  // Rolls that must never drop off the list even when they've sold nothing
  // lately — the cold-cache bug that kept losing Honey Soy Chicken at Arndale.
  alwaysRetain: ["Honey Soy Chicken"],
  // ---- the two LIVE signals (Lane BQ, 13 Sep 2026) ----------------------------
  // The live engine fetches these at run time (gov.uk + open-meteo) and consults
  // them in BOTH its multiplier functions: the target's flags, AND the dividing-out
  // of every history / previous-year day it averages. They ride the config so the
  // same values reach both places here without special-casing. EMPTY / NULL is
  // today's behaviour (no bank-holiday flag, no weather flag) — the kiosk reader
  // fills them from migrations 1320 / 1321 (src/lib/forecastSignals.js). Not tuning:
  // no weight, window, threshold or calendar value above changes.
  bankHolidays: [],   // ISO dates, gov.uk england-and-wales — every year the feed holds
  weather: null,      // { date, isRainy, isSunny, unavailableFlag } for ONE target date
};

// Site naming: the tablets say tc/arndale, the engine's config says TC/Arndale.
export function canonicalSite(site) {
  const s = String(site || "").toLowerCase();
  if (s === "tc" || s === "trafford" || s === "trafford centre") return "TC";
  if (s === "arndale") return "Arndale";
  if (s === "meadowhall") return "Meadowhall";
  if (s === "mcr" || s === "kitchen" || s === "manchester kitchen") return "Kitchen";
  return site;
}

// ---- pieces of the model ----------------------------------------------------

// A day the shop sold out early was a day demand was cut short, so that day's
// number under-reads real demand. Nudge it back up by how early it went.
export function applyStockoutUplift(demand, hoursBeforeClose, cfg = DEFAULT_CONFIG) {
  if (!(demand > 0)) return demand;
  if (hoursBeforeClose === null || hoursBeforeClose === undefined) return demand;
  if (!(hoursBeforeClose > 0)) return demand;
  let uplift = 0;
  for (const tier of cfg.stockoutTiers) {
    if (hoursBeforeClose > tier.hoursBeforeClose) { uplift = tier.uplift; break; }
  }
  // Sold out inside the last hour still counts — fall to the smallest tier.
  if (uplift === 0) uplift = cfg.stockoutTiers[cfg.stockoutTiers.length - 1].uplift;
  return Math.round(demand * (1 + uplift));
}

// A bank-holiday Monday shouldn't teach the model that Mondays are busy. Divide
// out whatever was going on that day before the number enters the average.
export function denormalise(demand, historicalMultiplier) {
  if (!(demand > 0)) return demand;
  if (!historicalMultiplier) return demand;
  return Math.round(demand / (1 + historicalMultiplier));
}

// Last week is the freshest signal, but one freak day shouldn't drag the whole
// forecast. If last week is more than 35% off the six-week average, try the week
// before; if that's odd too, fall back to the average itself.
export function filterOutlier(historyAdjusted, sixWeekAvg, cfg = DEFAULT_CONFIG) {
  if (!historyAdjusted || historyAdjusted.length === 0 || !(sixWeekAvg > 0)) return sixWeekAvg;
  const t = cfg.outlierThreshold;
  const lastWeek = historyAdjusted[0];
  if (Math.abs(lastWeek - sixWeekAvg) / sixWeekAvg <= t) return lastWeek;
  if (historyAdjusted.length >= 2) {
    const twoWeeks = historyAdjusted[1];
    if (Math.abs(twoWeeks - sixWeekAvg) / sixWeekAvg <= t) return twoWeeks;
  }
  return sixWeekAvg;
}

// The four-signal weighted average.
//
// ⚠️ READ THIS BEFORE "FIXING" THE DIVISOR. An earlier version of this comment
// called the no-previous-year path an "oddity", which reads like a bug and isn't
// one. What actually happens when there's no same-week-last-year figure is that
// its weight is **reassigned** onto the six-week average — it is not dropped. So
// the coefficients go from (2.0, 3.5, 1.5, 1.0) to (2.0, 5.0, —, 1.0), and both
// add up to 8.0. Dividing by 8.0 is therefore a correctly normalised weighted
// average in BOTH branches, not an unweighted sum over a stale constant.
//
// The distinction matters because the alternative — dropping the signal and
// renormalising over the remaining 6.5 — is a different model, not a bug fix,
// and would move every affected forecast (on a worked example: 163 portions
// becomes 177). That is a decision about what the kitchen makes, not a tidy-up.
//
// What this now does differently, and it is deliberately a no-op numerically:
// the divisor is computed from the coefficients actually used rather than
// hardcoded from all four weights. Identical output today, but it stays correct
// if someone edits a weight in `forecast_config`, and it makes the "the weight
// is reassigned, not dropped" fact visible in the code instead of a comment.
export function weightedBase({ lastSameDay, sixWeekAvg, prevYear, baseline }, cfg = DEFAULT_CONFIG) {
  const w = cfg.weights;
  const hasPrevYear = Boolean(prevYear);

  // (signal value, weight actually applied) — the single place the reassignment
  // happens, so the divisor below can never disagree with the numerator.
  const terms = hasPrevYear
    ? [[lastSameDay, w.lastSameDay], [sixWeekAvg, w.sixWeekAvg], [prevYear, w.prevYear], [baseline, w.baseline]]
    : [[lastSameDay, w.lastSameDay], [sixWeekAvg, w.sixWeekAvg + w.prevYear], [baseline, w.baseline]];

  let sum = 0, activeWeight = 0;
  for (const [value, weight] of terms) { sum += value * weight; activeWeight += weight; }
  if (activeWeight <= 0) return Math.round(baseline ?? 0);   // can't happen with sane config; never divide by zero
  return Math.round(sum / activeWeight);
}

// ---- context flags ----------------------------------------------------------
const isoInRange = (d, a, b) => d >= a && d <= b;

export function isSchoolHoliday(iso, cfg = DEFAULT_CONFIG) {
  return cfg.schoolHolidays.some((h) => isoInRange(iso, h.start, h.end));
}

// Payday weekend = a Friday or Saturday in the last 7 days of the month.
export function isPayday(iso) {
  const d = new Date(iso + "T12:00:00Z");
  const dow = d.getUTCDay();
  if (dow !== 5 && dow !== 6) return false;
  const lastDay = new Date(Date.UTC(d.getUTCFullYear(), d.getUTCMonth() + 1, 0)).getUTCDate();
  return lastDay - d.getUTCDate() < 7;
}

// Black Friday = the last Friday of November; the Saturday after peaks too, and
// the Monday-to-Thursday before it gets a smaller lift.
export function blackFridayKind(iso) {
  const d = new Date(iso + "T12:00:00Z");
  const month = d.getUTCMonth();
  if (month !== 10 && month !== 11) return null;
  const year = d.getUTCFullYear();
  const bf = new Date(Date.UTC(year, 10, new Date(Date.UTC(year, 11, 0)).getUTCDate()));
  while (bf.getUTCDay() !== 5) bf.setUTCDate(bf.getUTCDate() - 1);
  const bfSat = new Date(bf.getTime() + 86400000);
  const weekMon = new Date(bf.getTime() - 4 * 86400000);
  const ymd = (x) => x.toISOString().slice(0, 10);
  if (iso === ymd(bf) || iso === ymd(bfSat)) return "peak";
  if (d.getTime() >= weekMon.getTime() && d.getTime() < bf.getTime()) return "week";
  return null;
}

// The bank-holiday calendar, read from the config (DEFAULT_CONFIG.bankHolidays is
// empty; the kiosk reader fills it from gov.uk via migration 1320). The live engine
// checks this list for the target AND for every history day it de-normalises, so it
// is consulted here — the one place both paths go through.
export function isBankHoliday(iso, cfg = DEFAULT_CONFIG) {
  const bh = cfg.bankHolidays;
  if (!bh) return false;
  return bh instanceof Set ? bh.has(iso) : Array.isArray(bh) && bh.includes(iso);
}

// Works out the day's multipliers. Weather and bank holidays come from outside
// (they're live API calls in the original), so they're passed in — that also
// makes a back-test reproducible, because we can replay the flags the live
// engine actually recorded rather than asking today's weather about a day in May.
// An explicit argument always wins; when one is not given, the config's live
// signals apply (Lane BQ): the bank-holiday calendar to ANY date, the weather
// reading ONLY to its own date — the live engine only ever fetches the target's
// weather, so a history day can never pick it up.
export function contextMultipliers(site, iso, { isRainy = null, isSunny = null, isBankHoliday: bankHolidayArg = null, weatherUnavailable = null } = {}, cfg = DEFAULT_CONFIG) {
  const s = canonicalSite(site);
  const ctx = cfg.context;
  const flags = [];
  let mult = 0;
  const add = (label, effect) => { mult += effect; flags.push({ label, effect }); };

  const wx = cfg.weather && String(cfg.weather.date).slice(0, 10) === iso ? cfg.weather : null;
  const rainy = isRainy ?? (wx ? wx.isRainy : null);
  const sunny = isSunny ?? (wx ? wx.isSunny : null);
  const bankHoliday = bankHolidayArg ?? isBankHoliday(iso, cfg);
  const wxUnavailable = weatherUnavailable ?? Boolean(wx && wx.unavailableFlag);

  if (rainy) add("Rain expected", ctx.rain[s] ?? 0);
  else if (sunny) { const e = ctx.sunny[s] ?? 0; mult += e; if (e !== 0) flags.push({ label: "Sunny day", effect: e }); }
  // The live engine's "we asked and got nothing" chip: effect 0, and only shown
  // when the target is within 14 days (it never comments on the far future).
  else if (wxUnavailable) flags.push({ label: "Weather unavailable", effect: 0 });

  if (bankHoliday) add("Bank Holiday", ctx.bankHoliday[s] ?? 0);
  if (isSchoolHoliday(iso, cfg)) add("School Holiday", ctx.schoolHoliday[s] ?? 0);
  if (isoInRange(iso, cfg.ramadan.start, cfg.ramadan.end)) add("Ramadan", ctx.ramadan[s] ?? 0);
  if (iso === "2026-03-15") add("Mother's Day", ctx.mothersDay[s] ?? 0);

  const bf = blackFridayKind(iso);
  if (bf === "peak") add("Black Friday Peak", ctx.blackFridayPeak[s] ?? 0);
  else if (bf === "week") add("Black Friday Week", ctx.blackFridayWeek[s] ?? 0);

  if (isPayday(iso)) add("Payday weekend", ctx.payday[s] ?? 0);

  const d = new Date(iso + "T12:00:00Z");
  if (d.getUTCMonth() === 0 && d.getUTCDate() >= 2 && d.getUTCDate() <= 14) {
    add("January Sales", ctx.januarySales[s] ?? 0);
  }
  return { totalMultiplier: mult, flags };
}

// ---- the roll split ---------------------------------------------------------
export function isExcludedItem(name) {
  if (!name) return true;
  const lower = String(name).toLowerCase();
  if (lower.includes("meal deal")) return true;
  if (/ in$/i.test(String(name))) return true;
  return [
    "miso soup", "monthly special", "cali", "sweet & sour prawn",
    "korean tofu", "buffalo chicken", "panko mushroom", "teriyaki",
    "korean chicken", "honey prawn", "salmon avo", "cooked tuna avo",
  ].includes(lower);
}

// Splits the day's total across rolls in the proportions they've been selling,
// with a floor for anything brand new (no history = it still needs a few made).
export function skuSplit(site, historicalMix, totalForecast, currentNames = null, cfg = DEFAULT_CONFIG) {
  const s = canonicalSite(site);
  const skuTotals = {};
  let grandTotal = 0;
  for (const [name, qty] of Object.entries(historicalMix || {})) {
    if (isExcludedItem(name)) continue;
    skuTotals[name] = (skuTotals[name] || 0) + qty;
    grandTotal += qty;
  }

  let names = (currentNames || []).filter((n) => !isExcludedItem(n));
  if (names.length === 0) names = Object.keys(skuTotals);
  // The always-retain list, applied the same way and at the same point.
  const have = new Set(names.map((n) => String(n).toLowerCase()));
  for (const keep of cfg.alwaysRetain) {
    if (!have.has(keep.toLowerCase()) && !isExcludedItem(keep)) { names.push(keep); have.add(keep.toLowerCase()); }
  }

  const newSkuMinimum = cfg.newSkuMinimum[s] ?? 8;
  const newSkuCount = names.filter((n) => !skuTotals[n]).length;
  const reserved = newSkuCount * newSkuMinimum;
  let available = totalForecast - reserved;
  if (available < 0) available = totalForecast;

  const result = [];
  let allocated = 0;
  for (const sku of names) {
    const hist = skuTotals[sku] || 0;
    let portions = hist > 0 && grandTotal > 0 ? Math.round(available * (hist / grandTotal)) : newSkuMinimum;
    if (portions < 1) portions = 1;
    result.push({
      name: sku,
      portions,
      percentage: grandTotal > 0 ? Math.round((hist / grandTotal) * 100) : 0,
      isNew: hist === 0,
    });
    allocated += portions;
  }

  // Rounding never lands exactly on the total — push the difference onto the
  // biggest established roll (never a brand-new one).
  if (result.length > 0 && allocated !== totalForecast) {
    let maxIdx = 0;
    for (let m = 1; m < result.length; m++) {
      if (result[m].portions > result[maxIdx].portions && !result[m].isNew) maxIdx = m;
    }
    result[maxIdx].portions += totalForecast - allocated;
  }

  const order = cfg.preferredOrder;
  result.sort((a, b) => {
    const ia = order.indexOf(a.name) === -1 ? 999 : order.indexOf(a.name);
    const ib = order.indexOf(b.name) === -1 ? 999 : order.indexOf(b.name);
    return ia - ib;
  });
  return { breakdown: result, names };
}

// ---- the whole thing --------------------------------------------------------
/**
 * Run the forecast for one shop, one day.
 *
 * signals: { lastSameDay, sixWeekAvg, prevYear, weeksOfData }  — already
 *          stockout-adjusted and de-normalised, exactly as the live engine has
 *          them at this point.
 * context: { isRainy, isSunny, isBankHoliday } or { flags: [...] } to replay a
 *          recorded set (which is what the back-test does).
 * waste:   { portionsWasted, portionsMade } from the same weekday last week —
 *          over 20% waste trims the forecast 5%.
 */
export function buildForecast({ site, date, signals, context = {}, waste = null, historicalMix = null, currentNames = null, config = DEFAULT_CONFIG }) {
  const s = canonicalSite(site);
  const iso = String(date).slice(0, 10);
  const dow = new Date(iso + "T12:00:00Z").getUTCDay();
  const warnings = [];

  const baseline = config.baselines[s]?.[dow] ?? 250;
  const sixWeekAvg = signals.sixWeekAvg ?? baseline;
  if (signals.sixWeekAvg == null) warnings.push("No historical data — using the day-of-week baseline only");
  const lastSameDay = signals.lastSameDay ?? sixWeekAvg;
  const prevYear = signals.prevYear ?? null;
  if (!prevYear) warnings.push("No previous-year data — that weight moves onto the rolling average");

  const base = weightedBase({ lastSameDay, sixWeekAvg, prevYear, baseline }, config);

  // Either replay recorded flags, or work them out.
  let multiplier, flags;
  if (Array.isArray(context.flags)) {
    flags = context.flags.map((f) => ({ label: f.label, effect: Number(f.effect ?? f.mult ?? 0) }));
    multiplier = flags.reduce((a, f) => a + f.effect, 0);
  } else {
    const c = contextMultipliers(s, iso, context, config);
    multiplier = c.totalMultiplier;
    flags = c.flags;
  }

  const afterContext = Math.round(base * (1 + multiplier));

  // The waste trim: more than a fifth of last week's same day binned → take 5%
  // off. Deliberately one-directional, exactly like the live engine (it can
  // trim, it never bumps).
  let afterWaste = afterContext;
  let wastageAdjustment = null;
  if (waste && waste.portionsMade > 0) {
    const ratio = waste.portionsWasted / waste.portionsMade;
    if (ratio > config.wasteThreshold) {
      afterWaste = Math.round(afterContext * (1 - config.wasteTrim));
      wastageAdjustment = { type: "trim", ratio, message: `Trimmed 5% — high wastage last ${["Sunday","Monday","Tuesday","Wednesday","Thursday","Friday","Saturday"][dow]}` };
    }
  }

  let finalDemand = afterWaste;
  const floor = config.floors[s];
  const minApplied = floor != null && finalDemand < floor;
  if (minApplied) { finalDemand = floor; warnings.push(`Below the ${s} floor — bumped to ${floor}`); }

  const tgtgBuffer = config.tgtgBuffer[s] ?? 38;
  const totalPortions = finalDemand + tgtgBuffer;

  const split = historicalMix ? skuSplit(s, historicalMix, totalPortions, currentNames, config) : { breakdown: [], names: [] };

  return {
    site: s,
    date: iso,
    model: "V2_4signal",
    signals: { lastSameDay, sixWeekAvg, prevYear, baseline, weeksOfData: signals.weeksOfData ?? null },
    weightedBase: base,
    contextMultiplier: multiplier,
    flags,
    afterContext,
    wastageAdjustment,
    afterWaste,
    minApplied,
    tgtgBuffer,
    totalPortions,
    rolls: Math.ceil(totalPortions / 2),
    skuBreakdown: split.breakdown.map((x) => ({ name: x.name, pct: x.percentage, portions: x.portions, rolls: Math.ceil(x.portions / 2), isSpecial: x.isNew })),
    warnings,
  };
}

// =============================================================================
// Meadowhall — the v4.5 variant
// =============================================================================
// Meadowhall does not run the V2 engine. It runs Adam's separate v4.5, which
// lives in its own container-bound script we deliberately did NOT go digging in
// (it is a parked, separate stream and the brief says stay out of it).
//
// So this is modelled from what v4.5 RECORDS about itself on every confirmation
// — 140 real days of its own workings — not from its source. What those records
// show, and it reproduces cleanly:
//
//   * FIVE signals, not four: last same day (2.0), six-week average (1.5),
//     a FOOTFALL estimate (3.0 — the heaviest signal, and something V2 has no
//     concept of at all), and the day-of-week baseline (0.5). Total weight 7.
//   * No previous-year signal at all.
//   * A different TooGoodToGo buffer: 30, not 38.
//   * It applies context to the UNROUNDED weighted average, where V2 rounds the
//     base first and then applies context. That one-step difference is worth a
//     portion or two a day.
//
// Honesty about the fit: on the 140 recorded days this reproduces the weighted
// base exactly 121 times and the final total 123 times, and EVERY remaining
// difference is exactly ±1 portion — never more. The last portion is a rounding
// order we can't pin down without sight of the v4.5 source. That is a real
// limit, stated rather than hidden, and one portion a day is well inside the
// noise of a forecast that moves in tens.
export const V45_CONFIG = {
  weights: { lastSameDay: 2.0, sixWeekAvg: 1.5, footfall: 3.0, baseline: 0.5 },
  totalWeight: 7.0,
  tgtgBuffer: 30,
};

export function buildForecastV45({ site, date, signals, context = {}, wastageAdj = 1, config = V45_CONFIG }) {
  const iso = String(date).slice(0, 10);
  const w = config.weights;
  const parts = [
    [signals.lastSameDay, w.lastSameDay],
    [signals.sixWeekAvg, w.sixWeekAvg],
    [signals.footfall, w.footfall],
    [signals.baseline, w.baseline],
  ];
  let sum = 0;
  for (const [v, weight] of parts) if (v != null) sum += Number(v) * weight;
  // Deliberately keep the unrounded value: v4.5 carries it into the context step.
  const rawBase = sum / config.totalWeight;

  let multiplier;
  if (Array.isArray(context.flags)) multiplier = context.flags.reduce((a, f) => a + Number(f.effect ?? f.mult ?? 0), 0);
  else multiplier = Number(context.totalMultiplier ?? 0);

  const afterContext = Math.round(rawBase * (1 + multiplier) * (wastageAdj ?? 1));
  const totalPortions = afterContext + (config.tgtgBuffer ?? 30);

  return {
    site: canonicalSite(site),
    date: iso,
    model: "v4.5",
    weightedBase: Math.round(rawBase),
    rawBase,
    contextMultiplier: multiplier,
    afterContext,
    tgtgBuffer: config.tgtgBuffer ?? 30,
    totalPortions,
    rolls: Math.ceil(totalPortions / 2),
  };
}

// Turns a forecast_config row set into the shape above, so the engine can be
// driven from the table instead of these defaults.
export function loadConfig(rows) {
  const cfg = structuredClone(DEFAULT_CONFIG);
  for (const r of rows || []) {
    if (r.scope === "weights") cfg.weights[r.key] = Number(r.value);
    else if (r.scope === "baseline") (cfg.baselines[r.site] ||= {})[Number(r.key)] = Number(r.value);
    else if (r.scope === "closing") (cfg.closingTimes[r.site] ||= {})[Number(r.key)] = Number(r.value);
    else if (r.scope === "context") (cfg.context[r.key] ||= {})[r.site] = Number(r.value);
    else if (r.scope === "floor") cfg.floors[r.site] = Number(r.value);
    else if (r.scope === "buffer") cfg.tgtgBuffer[r.site] = Number(r.value);
    else if (r.scope === "newsku") cfg.newSkuMinimum[r.site] = Number(r.value);
    else if (r.scope === "threshold") cfg[r.key] = Number(r.value);
  }
  return cfg;
}
